import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import HeaderGlobal from '../../components/HeaderGlobal';
import ProspectsTable from './ProspectsTable';
import InterestDetailsModal from './InterestDetailsModal';
import NuevoInteresadoModal from './NuevoInteresadoModal';
import styles from '../../styles/GestionInteresadosStyles/GestionInteresados.module.css';
import { getInteresados, createInteresado, deleteInteresado, actualizarEstadoInteresado } from '../../api/interesados';
import { getCampus } from '../../api/campus';

function GestionInteresadosScreen() {
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [openMode, setOpenMode] = useState('view');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [campusFilter, setCampusFilter] = useState('');
  const [campusList, setCampusList] = useState([]);
  const [filters, setFilters] = useState({
    campus: 'Todos',
    responsable: 'Todos',
    estado: 'Todos'
  });
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Mostrar toast temporal
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Cargar prospectos desde API
  const loadProspects = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20, no_pagination: true };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.estado_prospecto = statusFilter;
      if (campusFilter) params.id_campus = campusFilter;
      const data = await getInteresados(params);
      // Mapear datos del backend al formato del frontend
      const list = Array.isArray(data) ? data : (data?.results || []);
      const mapped = list.map(p => ({
        id: p.id_interesado,
        _raw: p,
        nombre: p.nombre_completo,
        idInstitucion: p.id_institucion,
        idCampus: p.id_campus,
        idPrograma: p.id_programa,
        idCicloIngreso: p.id_ciclo_ingreso,
        idResponsableSeguimiento: p.responsable_seguimiento,
        campus: p.campus_nombre || '—',
        responsable: p.responsable_nombre || '—',
        estado: p.estado_prospecto,
        telefono: p.telefono,
        email: p.email,
        fechaNacimiento: p.fecha_nacimiento,
        municipio: p.municipio_residencia || '—',
        nivelEstudios: p.nivel_estudios_actual || '—',
        institucion: p.institucion_nombre || '—',
        campusInteres: p.campus_nombre || '—',
        seccion: p.seccion_nombre || '—',
        programaInteres: p.programa_nombre || '—',
        cicloIngreso: p.ciclo_nombre || '—',
        comoSeEntero: p.como_se_entero || '—',
        institucionProcedencia: p.institucion_procedencia || '—',
        folio: p.folio,
        comentarios: p.comentarios || '',
      }));
      setProspects(mapped);
      if (data?.count) {
        setTotalPages(Math.ceil(data.count / 20));
      }
    } catch (err) {
      console.error('[GestionInteresados] Error cargando prospectos:', err);
      showToast('Error al cargar prospectos: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, showToast, statusFilter, campusFilter]);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  // Cargar lista de campus para el filtro superior
  useEffect(() => {
    getCampus()
      .then((data) => setCampusList(Array.isArray(data) ? data : []))
      .catch(() => setCampusList([]));
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleProspectClick = (prospect) => {
    setOpenMode('view');
    setSelectedProspect(prospect);
    setShowDetailsModal(true);
  };

  const handleProspectEdit = (prospect) => {
    setOpenMode('edit');
    setSelectedProspect(prospect);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedProspect(null);
    setOpenMode('view');
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleMassiveCommunication = () => {
    console.log('Enviar comunicación masiva');
  };

  // Crear nuevo interesado
  const handleCreateProspect = async (formData) => {
    try {
      // Preparar payload para el backend
      const payload = {
        nombre_completo: formData.nombre_completo,
        email: formData.email,
        telefono: formData.telefono,
        fecha_nacimiento: formData.fecha_nacimiento,
        municipio_residencia: formData.municipio_residencia || '',
        nivel_estudios_actual: formData.nivel_estudios_actual,
        institucion_procedencia: formData.institucion_procedencia || '',
        como_se_entero: formData.como_se_entero || '',
        id_institucion: parseInt(formData.id_institucion),
        id_campus: parseInt(formData.id_campus),
        id_ciclo_ingreso: parseInt(formData.id_ciclo_ingreso),
        estado_prospecto: formData.estado_prospecto || 'NUEVO',
        comentarios: formData.comentarios || '',
      };
      if (formData.id_programa) payload.id_programa = parseInt(formData.id_programa);
      if (formData.responsable_seguimiento) payload.responsable_seguimiento = parseInt(formData.responsable_seguimiento);
      await createInteresado(payload);
      showToast('Interesado registrado exitosamente');
      setShowCreateModal(false);
      loadProspects();
    } catch (err) {
      console.error('[GestionInteresados] Error creando interesado:', err);
      showToast('Error al registrar: ' + err.message, 'error');
    }
  };

  // Eliminar interesado
  const handleDeleteProspect = async (prospectId) => {
    if (!window.confirm('¿Estás seguro de eliminar este prospecto?')) return;
    try {
      await deleteInteresado(prospectId);
      showToast('Prospecto eliminado');
      loadProspects();
    } catch (err) {
      showToast('Error al eliminar: ' + err.message, 'error');
    }
  };

  // Actualizar estado
  const handleUpdateEstado = async (prospectId, nuevoEstado, comentario = '') => {
    try {
      await actualizarEstadoInteresado(prospectId, nuevoEstado, comentario);
      showToast(`Estado actualizado a ${nuevoEstado}`);
      loadProspects();
      // Actualizar modal si está abierto
      if (selectedProspect && selectedProspect.id === prospectId) {
        setSelectedProspect(prev => prev ? { ...prev, estado: nuevoEstado } : null);
      }
    } catch (err) {
      showToast('Error al actualizar estado: ' + err.message, 'error');
    }
  };

  // Filtrar prospectos localmente
  const filteredProspects = prospects.filter(prospect => {
    const matchesCampus = filters.campus === 'Todos' || prospect.campus === filters.campus;
    const matchesResponsable = filters.responsable === 'Todos' || prospect.responsable === filters.responsable;
    const matchesEstado = filters.estado === 'Todos' || prospect.estado === filters.estado;
    return matchesCampus && matchesResponsable && matchesEstado;
  });

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>
      
      <div className={styles.contentColumn}>
        <HeaderGlobal title="Gestión de Interesados" />
        <div className={styles.contentArea}>
          <div className={styles.contentWrapper}>
            {/* Toast notification */}
            {toast && (
              <div style={{
                position: 'fixed', top: 24, right: 24, zIndex: 9999,
                padding: '12px 24px', borderRadius: '8px',
                background: toast.type === 'error' ? '#f44336' : '#4caf50',
                color: '#fff', fontWeight: 600, fontSize: '14px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                animation: 'fadeIn 0.3s ease',
              }}>
                {toast.message}
              </div>
            )}


            {/* Search - mismo diseño que InscripcionNuevoScreen */}
            <div className={estudiantesStyles.searchSection}>
              <div className={estudiantesStyles.searchCard}>
                <div className={estudiantesStyles.searchRow} style={{ gridTemplateColumns: '1fr auto auto auto auto' }}>
                  <div className={estudiantesStyles.searchInputGroup} style={{ width: '100%' }}>
                    <svg
                      className={estudiantesStyles.searchIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                        stroke="#898d8d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      type="text"
                      className={estudiantesStyles.searchInput}
                      placeholder="Buscar Prospecto..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') handleSearch(e.target.value);
                      }}
                    />
                  </div>
                  <button
                    className={estudiantesStyles.buscarButtonEstudiantes}
                    style={{ marginLeft: 12 }}
                    onClick={() => handleSearch(searchTerm)}
                  >
                    Buscar
                  </button>
                  <button
                    className={estudiantesStyles.buscarButtonEstudiantes}
                    style={{ marginLeft: 8, background: '#4caf50', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => setShowCreateModal(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Registrar Interesado
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 8 }}>
                    <span style={{ color: '#002f6c', fontWeight: 600, fontSize: '13px' }}>Campus:</span>
                    <select
                      className={styles.selectField}
                      value={campusFilter}
                      onChange={(e) => {
                        setPage(1);
                        setCampusFilter(e.target.value);
                      }}
                      style={{ minWidth: '160px', height: '38px' }}
                    >
                      <option value="">Todos</option>
                      {campusList.map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 8 }}>
                    <span style={{ color: '#002f6c', fontWeight: 600, fontSize: '13px' }}>Estatus:</span>
                    <select
                      className={styles.selectField}
                      value={statusFilter}
                      onChange={(e) => {
                        setPage(1);
                        setStatusFilter(e.target.value);
                      }}
                      style={{ minWidth: '170px', height: '38px' }}
                    >
                      <option value="">Todos</option>
                      <option value="NUEVO">NUEVO</option>
                      <option value="SEGUIMIENTO">SEGUIMIENTO</option>
                      <option value="EN_ESPERA">EN ESPERA</option>
                      <option value="EN_PROCESO">EN PROCESO</option>
                      <option value="ACEPTADO">ACEPTADO</option>
                      <option value="RECHAZADO">RECHAZADO</option>
                      <option value="PERDIO_INTERES">PERDIÓ INTERÉS</option>
                      <option value="MATRICULADO">MATRICULADO</option>
                      <option value="INSCRITO">INSCRITO</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Cargando prospectos...
              </div>
            )}

            {/* Prospects Table */}
            <ProspectsTable 
              prospects={filteredProspects}
              filters={filters}
              onFilterChange={handleFilterChange}
              onProspectClick={handleProspectClick}
              onEdit={handleProspectEdit}
              onDelete={handleDeleteProspect}
              onUpdateEstado={handleUpdateEstado}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
      
      {/* Details Modal */}
      {showDetailsModal && selectedProspect && (
        <InterestDetailsModal
          prospect={selectedProspect}
          isOpen={showDetailsModal}
          onClose={handleCloseModal}
          onUpdateEstado={handleUpdateEstado}
          onRefresh={loadProspects}
          openMode={openMode}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <NuevoInteresadoModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateProspect}
          title="Registrar Interesado"
        />
      )}
    </div>
  );
}

export default GestionInteresadosScreen;
